// @ts-ignore
import { PixiPlugin } from "gsap/all";
import { PopupToken } from "html-living-framework";
import * as HLF from "html-living-framework";
import * as springroll from "springroll";
import {GoogleAnalytics} from "springroll-google-analytics-plugin";
import * as CaptionData from "../json/close-caption.json";
import * as Constants from "./Data/Constants";
import { Culture } from "./Data/Culture";
import * as CopyJSON from "./Data/json/copy.json";
import * as SFX from "./Data/SoundEffect";
import * as Topics from "./Data/Topics";
import * as VO from "./Data/VoiceOver";
import { CaptionManager } from "./GameObjects/CaptionManager";
import { CharacterName } from "./GameObjects/Character";
import { LunaCaptionRenderer } from "./GameObjects/LunaCaptionRenderer";
import { VoiceOverManager } from "./GameObjects/VoiceOverManager";
import Gameplay from "./Gameplay";
import PausePopup from "./Popups/PausePopup";
import { AfterGame } from "./States/AfterGame";
import { Game } from "./States/Game";
import { Interstitial } from "./States/Interstitial";
import { LandingPage } from "./States/LandingPage";
import { SplashScreen } from "./States/SplashScreen";
import AudioPreloadManager from "./Utils/AudioPreloadManager";

// @ts-ignore
springroll.Application.uses(new GoogleAnalytics());

const pixiPlugin = PixiPlugin; // there. now it's not unused.

type OnDebugModeChanged = (pValue: boolean) => void;

Gameplay.STARTING_CULTURE = Math.floor(Math.random() * Culture.NumElements);
const CULTURE: Exclude<Culture, Culture.NumElements> = Gameplay.STARTING_CULTURE;

const SFX_VOLUME_MAX = 0.3;
const SOLO_VOLUME_MAX = 0.5;
const BGM_VOLUME_MAX = 0.15;
const VO_VOLUME_MAX = 0.75;

export class Application extends HLF.Application {
    private _debugMode: boolean = false;
    private readonly _debugModeChangedCallbacks: OnDebugModeChanged[];

    private _springroll!: springroll.Application;
    private _voiceOver!: VoiceOverManager;
    private _captions!: CaptionManager;
    private _paused: boolean = false;
    private constructor() {
        super({transparent: true});

        // this._loadManager.debug = true;
        // this._audioManager.debug = true;
        // this._stateManager.debug = true;
        // this._popupManager.debug = true;

        this._debugModeChangedCallbacks = new Array<OnDebugModeChanged>();

        this._audioManager.setCategoryVolume(HLF.AudioCategory.SFX, SFX_VOLUME_MAX);
        this._audioManager.setCategoryVolume(Constants.AUDIO_CATEGORY_SOLO, SOLO_VOLUME_MAX);
        this._audioManager.setCategoryVolume(HLF.AudioCategory.BGM, BGM_VOLUME_MAX);
        this._audioManager.setCategoryVolume(HLF.AudioCategory.VO, VO_VOLUME_MAX);

        // TODO Relish GM => Remove this for launch.
        window.addEventListener(HLF.Input.Events.KEY_DOWN, this.onKeyPressed.bind(this), false);
    }

    static get instance() {
        if (HLF.Application._instance === undefined) {
            HLF.Application._instance = new Application();
        }
        return HLF.Application._instance as Application;
    }

    public get voiceOver(): VoiceOverManager {
        return this._voiceOver;
    }

    public get captions() {
        return this._captions;
    }

    public get analytics() {
        return this._springroll.getPlugin("GoogleAnalytics") as (GoogleAnalytics & springroll.ApplicationPlugin);
    }

    public get debugMode(): boolean {
        return this._debugMode;
    }

    public init(): void {
        super.init();

        this.initSpringroll();

        this.copy.setData(CopyJSON.copy);

        this._resizeManager.sizeMax = new PIXI.Point(1663, 768);

        this._voiceOver = new VoiceOverManager();

        this._captions = new CaptionManager((CaptionData as any).default);
        this.stage.addChild(this._captions);

        this.onResize();
    }

    public update(): void {
        if (this._paused) {
            return;
        }
        else {
            super.update();
            const deltaTime: number = PIXI.ticker.shared.elapsedMS / 1000;
            this._captions.update(deltaTime);
            AudioPreloadManager.update();
        }
    }

    public destroy(pRemoveView?: boolean, pStageOptions?: boolean | PIXI.StageOptions): void {
        this._voiceOver.destroy();
        super.destroy(pRemoveView, pStageOptions);
    }

    public registerOnDebugModeChangedCallback(pCallback: OnDebugModeChanged): boolean {
        const index = this._debugModeChangedCallbacks.indexOf(pCallback);

        if (index === -1) {
            this._debugModeChangedCallbacks.push(pCallback);
            return true;
        }

        return false;
    }

    public unregisterOnDebugChangedCallback(pCallback: OnDebugModeChanged): void {
        const index = this._debugModeChangedCallbacks.indexOf(pCallback);

        if (index > -1) {
            this._debugModeChangedCallbacks.splice(index, 1);
        }
    }

    protected createSplashScreen(): HLF.SplashScreen {
        return new SplashScreen();
    }

    protected createAssetMap(): void {
        HLF.AssetMap.addAssetGroup(HLF.SplashScreen.NAME, [
            new HLF.AssetMapData("splash", HLF.AssetType.JPG),
            new HLF.AssetMapData(Constants.FONT_LUNA, HLF.AssetType.FONT),
        ]);

        const pads = [];
        for (let pad = 0; pad < 6; pad++) {
            for (let culture = 0; culture < 3; culture++) {
                for (let variant = 0; variant < 3; variant++) {
                    const filename = `pad_${pad}_${culture}_${variant}`;
                    pads.push(new HLF.AssetMapAudioData(filename, HLF.AssetType.AUDIO, HLF.AudioCategory.SFX));
                }
            }
        }

        HLF.AssetMap.addAssetGroup(Constants.STATE_LANDING_PAGE, [
            new HLF.AssetMapData("splash", HLF.AssetType.JPG),
            new HLF.AssetMapAudioData(VO.U1, HLF.AssetType.AUDIO, HLF.AudioCategory.VO),
            new HLF.AssetMapAudioData(VO.U2, HLF.AssetType.AUDIO, HLF.AudioCategory.VO),

            // preload assets for the game state so there's no delay after pressing the start button
            new HLF.AssetMapData("menu-bg", HLF.AssetType.JPG),
            ...Constants.SPRITESHEET_GAME.map((page) => new HLF.AssetMapData(page, HLF.AssetType.TEXTURE_ATLAS)),
            new HLF.AssetMapData("instrument-keyboard-bg-00", HLF.AssetType.PNG),
            new HLF.AssetMapData("instrument-keyboard-bg-01", HLF.AssetType.PNG),
            new HLF.AssetMapData("instrument-keyboard-bg-02", HLF.AssetType.PNG),
            new HLF.AssetMapData(Constants.SPRITESHEET_INSTRUMENT_KEYBOARD, HLF.AssetType.TEXTURE_ATLAS),
            new HLF.AssetMapData("note-particles", HLF.AssetType.TEXTURE_ATLAS),
            new HLF.AssetMapData("globe", HLF.AssetType.TEXTURE_ATLAS),
            new HLF.AssetMapSpineData("andy", "combined"),
            new HLF.AssetMapSpineData("carmen", "combined"),
            new HLF.AssetMapSpineData("luna", "combined"),
            new HLF.AssetMapSpineData("leo", "combined"),
            new HLF.AssetMapAudioData("backing_loop_0", HLF.AssetType.AUDIO, HLF.AudioCategory.BGM),
            new HLF.AssetMapAudioData("backing_loop_1", HLF.AssetType.AUDIO, HLF.AudioCategory.BGM),
            ...pads,
            new HLF.AssetMapAudioData(`solo_0_${CULTURE}_A`, HLF.AssetType.AUDIO, Constants.AUDIO_CATEGORY_SOLO),
            new HLF.AssetMapAudioData(`solo_1_${CULTURE}_A`, HLF.AssetType.AUDIO, Constants.AUDIO_CATEGORY_SOLO),
            new HLF.AssetMapAudioData(`solo_2_${CULTURE}_A`, HLF.AssetType.AUDIO, Constants.AUDIO_CATEGORY_SOLO),
            new HLF.AssetMapAudioData(VO.O2, HLF.AssetType.AUDIO, HLF.AudioCategory.VO),
            new HLF.AssetMapAudioData(SFX.BUTTON_APPEAR, HLF.AssetType.AUDIO, HLF.AudioCategory.SFX),
            new HLF.AssetMapAudioData(SFX.BALLOON_SPAWN, HLF.AssetType.AUDIO, HLF.AudioCategory.SFX),
            new HLF.AssetMapAudioData(SFX.CONFETTI_SPAWN, HLF.AssetType.AUDIO, HLF.AudioCategory.SFX),
            new HLF.AssetMapAudioData(SFX.BALLOON_POP_1, HLF.AssetType.AUDIO, HLF.AudioCategory.SFX),
            new HLF.AssetMapAudioData(SFX.BALLOON_POP_2, HLF.AssetType.AUDIO, HLF.AudioCategory.SFX),
            new HLF.AssetMapAudioData(SFX.DANCE_UNLOCKED, HLF.AssetType.AUDIO, HLF.AudioCategory.SFX),
            new HLF.AssetMapAudioData(SFX.GLOBE_CLICKED, HLF.AssetType.AUDIO, HLF.AudioCategory.SFX),
            new HLF.AssetMapAudioData(SFX.CAMERA_BUTTON_APPEAR, HLF.AssetType.AUDIO, HLF.AudioCategory.SFX),
            new HLF.AssetMapAudioData(SFX.CAMERA_CLICKED, HLF.AssetType.AUDIO, HLF.AudioCategory.SFX),
            new HLF.AssetMapAudioData(SFX.PHOTO_APPEAR, HLF.AssetType.AUDIO, HLF.AudioCategory.SFX),
            ...SFX.IMPACTS.map((s) => new HLF.AssetMapAudioData(s, HLF.AssetType.AUDIO, HLF.AudioCategory.SFX)),

            // preload assets for the AfterGame state
            ...AfterGame.assets,
        ]);
        HLF.AssetMap.addAssetGroup(Constants.STATE_GAME, [
            new HLF.AssetMapData("menu-bg", HLF.AssetType.JPG),
            ...Constants.SPRITESHEET_GAME.map((page) => new HLF.AssetMapData(page, HLF.AssetType.TEXTURE_ATLAS)),
            new HLF.AssetMapData("instrument-keyboard-bg-00", HLF.AssetType.PNG),
            new HLF.AssetMapData("instrument-keyboard-bg-01", HLF.AssetType.PNG),
            new HLF.AssetMapData("instrument-keyboard-bg-02", HLF.AssetType.PNG),
            new HLF.AssetMapData(Constants.SPRITESHEET_INSTRUMENT_KEYBOARD, HLF.AssetType.TEXTURE_ATLAS),
            new HLF.AssetMapData("note-particles", HLF.AssetType.TEXTURE_ATLAS),
            new HLF.AssetMapData("globe", HLF.AssetType.TEXTURE_ATLAS),
            new HLF.AssetMapData("andy", HLF.AssetType.SPINE),
            new HLF.AssetMapData("carmen", HLF.AssetType.SPINE),
            new HLF.AssetMapData("luna", HLF.AssetType.SPINE),
            new HLF.AssetMapData("leo", HLF.AssetType.SPINE),
            new HLF.AssetMapAudioData("backing_loop_0", HLF.AssetType.AUDIO, HLF.AudioCategory.BGM),
            new HLF.AssetMapAudioData("backing_loop_1", HLF.AssetType.AUDIO, HLF.AudioCategory.BGM),
            ...pads,
            new HLF.AssetMapAudioData(`solo_0_${CULTURE}_A`, HLF.AssetType.AUDIO, Constants.AUDIO_CATEGORY_SOLO),
            new HLF.AssetMapAudioData(`solo_1_${CULTURE}_A`, HLF.AssetType.AUDIO, Constants.AUDIO_CATEGORY_SOLO),
            new HLF.AssetMapAudioData(`solo_2_${CULTURE}_A`, HLF.AssetType.AUDIO, Constants.AUDIO_CATEGORY_SOLO),
            new HLF.AssetMapAudioData(VO.O2, HLF.AssetType.AUDIO, HLF.AudioCategory.VO),
            new HLF.AssetMapAudioData(SFX.BUTTON_APPEAR, HLF.AssetType.AUDIO, HLF.AudioCategory.SFX),
            new HLF.AssetMapAudioData(SFX.BALLOON_SPAWN, HLF.AssetType.AUDIO, HLF.AudioCategory.SFX),
            new HLF.AssetMapAudioData(SFX.CONFETTI_SPAWN, HLF.AssetType.AUDIO, HLF.AudioCategory.SFX),
            new HLF.AssetMapAudioData(SFX.BALLOON_POP_1, HLF.AssetType.AUDIO, HLF.AudioCategory.SFX),
            new HLF.AssetMapAudioData(SFX.BALLOON_POP_2, HLF.AssetType.AUDIO, HLF.AudioCategory.SFX),
            new HLF.AssetMapAudioData(SFX.DANCE_UNLOCKED, HLF.AssetType.AUDIO, HLF.AudioCategory.SFX),
            new HLF.AssetMapAudioData(SFX.GLOBE_CLICKED, HLF.AssetType.AUDIO, HLF.AudioCategory.SFX),
            new HLF.AssetMapAudioData(SFX.CAMERA_BUTTON_APPEAR, HLF.AssetType.AUDIO, HLF.AudioCategory.SFX),
            new HLF.AssetMapAudioData(SFX.CAMERA_CLICKED, HLF.AssetType.AUDIO, HLF.AudioCategory.SFX),
            new HLF.AssetMapAudioData(SFX.PHOTO_APPEAR, HLF.AssetType.AUDIO, HLF.AudioCategory.SFX),
            ...SFX.IMPACTS.map((s) => new HLF.AssetMapAudioData(s, HLF.AssetType.AUDIO, HLF.AudioCategory.SFX)),

            // preload assets for the AfterGame state
            ...AfterGame.assets,
        ]);

        HLF.AssetMap.addAssetGroup(AfterGame.ID, AfterGame.assets);
    }

    protected registerLoadingScreens(): void {
        this._loadManager.registerLoadScreen(Constants.LOAD_INTERSTITIAL, new Interstitial());
    }

    protected registerPopups(): void {
        this._popupManager.registerPopup(() => new PausePopup(), "pause");
    }

    protected registerStates(): void {
        this._stateManager.registerState(Constants.STATE_LANDING_PAGE, () => new LandingPage());
        this._stateManager.registerState(Constants.STATE_GAME, () => new Game());
        this._stateManager.registerState(AfterGame.ID, AfterGame.Create);
    }

    public get requiredAssets(): HLF.AssetMapData[] {
        const assets = new Array<HLF.AssetMapData>();

        assets.push(new HLF.AssetMapData(Constants.SPRITESHEET_UI, HLF.AssetType.TEXTURE_ATLAS));
        assets.push(new HLF.AssetMapData(Constants.FONT_LUNA, HLF.AssetType.FONT));
        assets.push(new HLF.AssetMapData(Constants.FONT_GEL_LIGHT_22, HLF.AssetType.FONT));
        assets.push(new HLF.AssetMapData(Constants.FONT_GEL_MED_22, HLF.AssetType.FONT));
        assets.push(new HLF.AssetMapData(Constants.FONT_GEL_MED_63, HLF.AssetType.FONT));
        assets.push(new HLF.AssetMapData(Constants.FONT_OUTLINED, HLF.AssetType.FONT));

        return assets;
    }

    protected onLoadRequiredAssetsComplete(): void {
        super.onLoadRequiredAssetsComplete();

        this._captions.setRenderer(new LunaCaptionRenderer(Constants.FONT_GEL_MED_63, 70));
        this._captions.onResize(this._size);

        if (Constants.DEBUG_DEFAULT_STATE === undefined) {
            PubSub.publishSync(HLF.STATE_LOAD_STATE, new HLF.StateToken(
                Constants.STATE_LANDING_PAGE, HLF.SplashScreen.NAME, ...HLF.StateToken.TRANSITION_FIRST_VIEW,
            ));
        }
        else {
            PubSub.publishSync(HLF.STATE_LOAD_STATE, new HLF.StateToken(
                Constants.DEBUG_DEFAULT_STATE, HLF.SplashScreen.NAME, ...HLF.StateToken.TRANSITION_FIRST_VIEW,
            ));
        }
    }

    // TODO Relish GM => Remove this for launch.
    private onKeyPressed(pEvent: KeyboardEvent): void {
        // if (pEvent.keyCode === HLF.Input.KeyCodes.P) {
        //     this.onPause(!this._paused);
        // }
        if (pEvent.code === "KeyH") {
            if (this._debugMode) {
                this._hitAreaRenderer.active = !this._hitAreaRenderer.active;
                this._hitAreaRenderer.interval = 1;
            }
        }
        else if (pEvent.code === "Backquote") {
            this._debugMode = !this._debugMode;
            this._debugModeChangedCallbacks.forEach((callback) => {
                callback(this._debugMode);
            });
        }
    }

    private initSpringroll(): void {
        this._springroll = new springroll.Application({
            features: {
                captions: true,
                sound: true,
                vo: true,
                music: true,
                sfx: true,
            },
        });

        this._springroll.state.ready.subscribe(() => {
            this.onSpringrollInitialized();
        });

        this._springroll.state.pause.subscribe((isPaused: boolean) => {
            this.onPause(isPaused);
        });

        this._springroll.state.playOptions.subscribe((playOptions: object) => {
            console.log("New playOptions value set to", playOptions);
        });

        this._springroll.state.captionsMuted.subscribe((result: boolean) => {
            this.onCaptionsMuted(result);
        });

        this._springroll.state.soundVolume.subscribe((result: number) => {
            this.onSoundVolume(result);
        });

        this._springroll.state.musicVolume.subscribe((result: number) => {
            this.onMusicVolume(result * BGM_VOLUME_MAX);
        });

        this._springroll.state.voVolume.subscribe((result: number) => {
            this.onVOVolume(result * VO_VOLUME_MAX);
        });

        this._springroll.state.sfxVolume.subscribe((result: number) => {
            this.onSFXVolume(result * SFX_VOLUME_MAX);
        });
    }

    private onSpringrollInitialized(): void {
        // override
    }

    // Springroll message listeners
    private onCaptionsMuted(pMuted: boolean): void {
        this._captions.active = !pMuted;
    }

    private onSoundVolume(pResults: number): void {
        this.audio.masterVolume = pResults;
    }

    private onVOVolume(pResults: number): void {
        this.audio.setCategoryVolume(HLF.AudioCategory.VO, pResults);
    }

    private onMusicVolume(pResults: number): void {
        this.audio.setCategoryVolume(HLF.AudioCategory.BGM, pResults);
    }

    private onSFXVolume(pResults: number): void {
        this.audio.setCategoryVolume(HLF.AudioCategory.SFX, pResults);
    }

    private onPause(pIsPaused: boolean): void {
        console.log("paused: " + pIsPaused);
        const wasPaused = this._paused;
        if (pIsPaused && !wasPaused) {
            PubSub.publishSync(Topics.GAME_PAUSED, undefined);
            PubSub.publishSync(HLF.SHOW_POPUP, new PopupToken("pause", undefined, "static", false));
        }
        else if (wasPaused && !pIsPaused) {
            PubSub.publishSync(HLF.HIDE_POPUP, "pause");
            PubSub.publishSync(Topics.GAME_UNPAUSED, undefined);
        }
        this._paused = pIsPaused;
    }
}
